import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Heart, Users, Star, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/Button';
export function AboutPage() {
  return <div className="w-full" data-id="element-99">
      {/* Hero */}
      <section className="relative py-32 bg-primary text-white overflow-hidden" data-id="element-100">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-white/5 skew-x-12 translate-x-1/4" data-id="element-101" />
        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-102">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl" data-id="element-103">
            <h1 className="text-5xl lg:text-7xl font-display font-extrabold mb-8" data-id="element-104">
              Our Story & Mission
            </h1>
            <p className="text-xl text-white/80 leading-relaxed" data-id="element-105">
              Birds Nest Independent Living was founded on the simple belief
              that housing is the foundation of health, dignity, and
              independence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission/Vision */}
      <section className="py-32 px-6" data-id="element-106">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20" data-id="element-107">
          <div className="space-y-12" data-id="element-108">
            <div data-id="element-109">
              <h2 className="text-4xl font-display font-bold text-charcoal mb-6" data-id="element-110">
                Our Mission
              </h2>
              <p className="text-lg text-muted leading-relaxed" data-id="element-111">
                To provide high-quality, supportive independent living
                environments that empower adults to lead fulfilling lives within
                their communities. We strive to be the bridge that connects
                individuals to stable housing and the resources they need to
                thrive.
              </p>
            </div>
            <div data-id="element-112">
              <h2 className="text-4xl font-display font-bold text-charcoal mb-6" data-id="element-113">
                Our Vision
              </h2>
              <p className="text-lg text-muted leading-relaxed" data-id="element-114">
                To become Maryland's leading provider of supportive housing,
                recognized for our commitment to resident dignity, community
                integration, and excellence in property management.
              </p>
            </div>
          </div>
          <div className="relative" data-id="element-115">
            <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=1000" alt="Team Meeting" className="rounded-2xl shadow-2xl w-full aspect-square object-cover" data-id="element-116" />
            <div className="absolute -bottom-10 -left-10 bg-accent p-8 rounded-2xl shadow-xl hidden md:block" data-id="element-117">
              <div className="text-4xl font-bold text-charcoal mb-1" data-id="element-118">100%</div>
              <div className="text-sm font-bold text-charcoal/70 uppercase tracking-widest" data-id="element-119">
                Commitment to Care
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-32 bg-background px-6" data-id="element-120">
        <div className="max-w-7xl mx-auto text-center mb-20" data-id="element-121">
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-charcoal mb-6" data-id="element-122">
            Our Core Values
          </h2>
          <p className="text-muted text-xl max-w-2xl mx-auto" data-id="element-123">
            The principles that guide every decision we make and every home we
            manage.
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-124">
          {[{
          title: 'Respect',
          icon: Heart,
          desc: 'Treating every resident with the dignity and respect they deserve.'
        }, {
          title: 'Safety',
          icon: ShieldCheck,
          desc: 'Maintaining secure, clean, and well-managed environments.'
        }, {
          title: 'Independence',
          icon: Star,
          desc: 'Empowering residents to live autonomously with appropriate support.'
        }, {
          title: 'Integrity',
          icon: CheckCircle2,
          desc: 'Operating with transparency and honesty in all our partnerships.'
        }, {
          title: 'Community',
          icon: Users,
          desc: 'Fostering a sense of belonging and mutual support in every home.'
        }, {
          title: 'Compassion',
          icon: Heart,
          desc: 'Leading with empathy and understanding for the unique journeys of our residents.'
        }].map(value => <div key={value.title} className="bg-white p-10 rounded-2xl shadow-premium border border-gray-100 hover:shadow-premium-hover transition-shadow" data-id="element-125">
              <div className="w-16 h-16 bg-primary/5 rounded-2xl flex items-center justify-center text-primary mb-8" data-id="element-126">
                <value.icon size={32} data-id="element-127" />
              </div>
              <h3 className="text-2xl font-bold text-charcoal mb-4" data-id="element-128">
                {value.title}
              </h3>
              <p className="text-muted leading-relaxed" data-id="element-129">{value.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-32 px-6" data-id="element-130">
        <div className="max-w-7xl mx-auto" data-id="element-131">
          <div className="text-center mb-20" data-id="element-132">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-charcoal mb-6" data-id="element-133">
              Who We Serve
            </h2>
            <p className="text-muted text-xl max-w-2xl mx-auto" data-id="element-134">
              Birds Nest provides housing for a diverse range of adults seeking
              stability and community.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-135">
            {['Adults seeking independent living', 'Veterans', 'Transitioning from treatment', 'Re-entering the community', 'Working adults', 'Adults with stable medical needs'].map(item => <div key={item} className="flex items-center p-6 bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-136">
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center text-accent mr-4 shrink-0" data-id="element-137">
                  <CheckCircle2 size={20} data-id="element-138" />
                </div>
                <span className="font-bold text-charcoal" data-id="element-139">{item}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6" data-id="element-140">
        <div className="max-w-7xl mx-auto bg-primary rounded-3xl p-12 lg:p-20 text-center text-white relative overflow-hidden" data-id="element-141">
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" data-id="element-142" />
          <h2 className="text-4xl lg:text-5xl font-display font-bold mb-8 relative z-10" data-id="element-143">
            Join Our Community
          </h2>
          <p className="text-xl text-white/70 mb-12 max-w-2xl mx-auto relative z-10" data-id="element-144">
            Whether you're looking for a home or looking to partner with us,
            we'd love to hear from you.
          </p>
          <div className="flex flex-wrap justify-center gap-6 relative z-10" data-id="element-145">
            <Button variant="accent" size="lg" data-id="element-146">
              Check Availability
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary" data-id="element-147">
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </div>;
}